/**
 * resolveTranslatedSelectionPhrases.ts — PLAT-10214 (T6, epic PLAT-10209),
 * reworked id-only in PLAT-10646.
 *
 * Resolve a DOM text selection in a TRANSLATED transcript view to the
 * contiguous run of active translation fragments (`TranslationPhrase[]`) it
 * covers. The shared entry for both span-replace surfaces (workspace + inline
 * pill), the selection-precedence guard, and the extended context menu.
 *
 * Identity is STABLE ELEMENT UUID only. Every translated word span is stamped
 * with `data-source-uuid` (the lead element's `source_uuid ?? id`), and that
 * uuid is by construction one of the owning phrase's `originalElementIds` — so
 * a span resolves straight to its phrase through `originalElementIds`, with no
 * `initial_index` / positional bookkeeping. This is what lets reused/reordered
 * clips (whose `initial_index` is renumbered, often to -1) resolve correctly.
 *
 * Contiguity is judged by DOM ADJACENCY, not source-index math: the translated
 * column renders exactly one span per phrase in reading order, so a run is
 * contiguous iff the selected phrase-spans form an unbroken block — no visible
 * translated word inside the dragged extent was skipped. Spans that resolve to
 * no phrase (content-less whitespace) are transparent.
 *
 * DOM scoping (CLAUDE.md #12 — duplicate `#transcript-scrollable-container`):
 * the caller passes the LOCAL container element (a ref to the actual translated
 * column). All `querySelectorAll` here is scoped to THAT container, never
 * `document`, so a second translated transcript instance is never read.
 *
 * No React, no MobX — pure DOM + data so it unit-tests with a jsdom container.
 */

import type { TranslationPhrase } from '@src/types.ts/translation';

/** Why a selection did not resolve to a replaceable span. */
export type SelectionRejectReason =
  | 'no-selection' // nothing selected / collapsed caret
  | 'no-container' // the scoped container was null
  | 'outside-container' // the selection isn't inside the container
  | 'non-contiguous' // the touched phrase-spans don't form a single run
  | 'no-phrases'; // no active phrase owns the touched spans

export interface ResolvedSelection {
  ok: true;
  /** The covered active fragments, in document (reading) order. */
  phrases: TranslationPhrase[];
}

export interface UnresolvedSelection {
  ok: false;
  reason: SelectionRejectReason;
}

export type SelectionResolution = ResolvedSelection | UnresolvedSelection;

/**
 * Map every stable element UUID an active phrase covers back to that phrase.
 * The active set has exactly one phrase per source element (coverage invariant
 * D10), so a shared uuid is unambiguous. Built from `originalElementIds`, the
 * durable content-only key; whitespace phrases (no ids) are naturally absent,
 * so their spans resolve to no phrase and stay transparent.
 */
function buildPhraseByUuid(
  activePhrases: readonly TranslationPhrase[],
): Map<string, TranslationPhrase> {
  const phraseByUuid = new Map<string, TranslationPhrase>();
  for (const phrase of activePhrases) {
    if (!phrase.originalElementIds) continue;
    for (const id of phrase.originalElementIds) phraseByUuid.set(id, phrase);
  }
  return phraseByUuid;
}

/**
 * Walk the rendered word spans in `container` in document order, resolving each
 * to its owning phrase by `data-source-uuid` and recording whether the range
 * touches it. Spans that resolve to no phrase are dropped (transparent). Reads
 * ONLY the uuid attribute — no `data-initial-index`.
 *
 * `Range.intersectsNode` is the precise test (a partially-selected word counts,
 * matching how a user dragging mid-word still means "this word"). When it's
 * unavailable (older jsdom), fall back to "the node is within the range's
 * common ancestor" — coarse but only used in the test environment.
 */
function collectResolvedSpans(
  container: HTMLElement,
  range: Range,
  phraseByUuid: ReadonlyMap<string, TranslationPhrase>,
): { phrase: TranslationPhrase; selected: boolean }[] {
  const out: { phrase: TranslationPhrase; selected: boolean }[] = [];
  const spans = container.querySelectorAll<HTMLElement>('[data-source-uuid]');
  spans.forEach(span => {
    const uuid = span.getAttribute('data-source-uuid');
    if (uuid === null) return;
    const phrase = phraseByUuid.get(uuid);
    if (!phrase) return; // transparent (whitespace / not an active phrase)
    const selected =
      typeof range.intersectsNode === 'function'
        ? range.intersectsNode(span) && coversText(range, span)
        : range.commonAncestorContainer.contains(span);
    out.push({ phrase, selected });
  });
  return out;
}

/**
 * Whether `range` covers at least one visible character of `span`.
 * `intersectsNode` also counts a span the range merely touches at its edge —
 * a range starting at the END of the previous word's text, or ending at
 * offset 0 of the next. With whitespace spans between words that edge lands on
 * a transparent span, but CJK translations render phrase spans back to back,
 * so a drag starting just after 。 silently pulled in the previous phrase, and
 * Fix Translation opened on that phrase instead of the selected one.
 */
function coversText(range: Range, span: HTMLElement): boolean {
  if (typeof range.compareBoundaryPoints !== 'function') return true;
  const overlap = span.ownerDocument.createRange();
  overlap.selectNodeContents(span);
  if (range.compareBoundaryPoints(Range.START_TO_START, overlap) > 0) {
    overlap.setStart(range.startContainer, range.startOffset);
  }
  if (range.compareBoundaryPoints(Range.END_TO_END, overlap) < 0) {
    overlap.setEnd(range.endContainer, range.endOffset);
  }
  return overlap.toString().trim().length > 0;
}

/**
 * Reduce the ordered phrase-spans to the covered run and assert contiguity by
 * DOM adjacency. Let the selected spans occupy positions `first…last` in the
 * phrase-span sequence; the run is contiguous iff every phrase-span between
 * them is also selected (no visible word skipped). Returns the covered phrases
 * de-duped in document order.
 */
function resolveCoveredPhrases(
  resolved: readonly { phrase: TranslationPhrase; selected: boolean }[],
): SelectionResolution {
  let first = -1;
  let last = -1;
  for (let i = 0; i < resolved.length; i++) {
    if (!resolved[i].selected) continue;
    if (first === -1) first = i;
    last = i;
  }
  if (first === -1) return { ok: false, reason: 'no-phrases' };
  for (let i = first; i <= last; i++) {
    if (!resolved[i].selected) return { ok: false, reason: 'non-contiguous' };
  }

  const seen = new Set<TranslationPhrase>();
  const phrases: TranslationPhrase[] = [];
  for (let i = first; i <= last; i++) {
    const { phrase } = resolved[i];
    if (seen.has(phrase)) continue;
    seen.add(phrase);
    phrases.push(phrase);
  }
  return { ok: true, phrases };
}

/**
 * Resolve the current (or supplied) selection within `container` to the covered
 * active fragments. Returns a discriminated result so the caller can branch on
 * success vs. the precise rejection reason (collapsed selection, outside the
 * pane, scattered run, …) without throwing.
 *
 * @param container   The LOCAL translated scroll container (a ref's `.current`).
 *                    Pass the actual element, never look it up by a shared id.
 * @param activePhrases The active translation fragments (`active !== false`).
 * @param selection   Defaults to `window.getSelection()`; injectable for tests.
 */
export function resolveTranslatedSelectionPhrases(
  container: HTMLElement | null,
  activePhrases: readonly TranslationPhrase[],
  selection: Selection | null = typeof window !== 'undefined'
    ? window.getSelection()
    : null,
): SelectionResolution {
  if (!container) return { ok: false, reason: 'no-container' };
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    return { ok: false, reason: 'no-selection' };
  }

  const range = selection.getRangeAt(0);
  // The selection must be anchored inside our container (CLAUDE.md #12: a
  // selection in the OTHER transcript instance must not resolve here).
  if (!container.contains(range.commonAncestorContainer)) {
    return { ok: false, reason: 'outside-container' };
  }

  const phraseByUuid = buildPhraseByUuid(activePhrases);
  const resolved = collectResolvedSpans(container, range, phraseByUuid);
  return resolveCoveredPhrases(resolved);
}
